import { useTranslation } from 'next-i18next';
import { WidenImage } from '@hub/common/Widen/WidenImage';
import { useDebouncedCallback } from 'use-debounce';
import { IbhFilterKeys } from '@hub/libs/constants';
import { useEffect } from 'react';
import { useDimensionsStore } from '@hub/hooks/states/useIbhDimensionsValuesStore';
import { useNextHashQueryParams } from '@hub/hooks/useNextQueryParams';
import type { DimensionKey, IbhDimensions } from '@hub/bearing-hub/common/dimensions/dimensions';
import { dimensionKey, ShapeDimensionsDefinitionForm } from '@hub/bearing-hub/common/dimensions/dimensions';
import { AngleCode, getUnitLabel } from '@icommerce/units';
import { IbhShape } from '@igus/iglidur-bearing-hub-api/src/data-contracts';
import { BasicNumberInput } from '@hub/common/BasicNumberInput/BasicNumberInput';
import { Switch } from '../common/Switch/Switch';
import { FilterResetLink } from '@hub/common/FilterResetLink/FilterResetLink';
import { IbhEventCategory, TrackedIbhEventActions } from '../common/tracking.config';
import { usePiwikPro } from '@piwikpro/next-piwik-pro';
import { type ToastPosition, ToastType, useToasts } from '@hub/hooks/useToasts';
import { Icon, IconColor, IconID } from '@hub/common/Icon/Icon';
import { Button, TYPE } from '@hub/common/Button/Button';
import { UnitSystem } from '@igus/kopla-domain';
import { getLengthCode } from '@hub/bearing-hub/common/useUnitSystem/useUnitSystem';

const MAX_SLOT_ANGLE = 25;

type DimensionInput = {
  title: string;
  attribute: DimensionKey;
  attributeText: string;
  unit: string;
  validation: (value: string) => boolean;
};

interface DimensionFormProps {
  shapeKey: IbhShape;
  widenShortKey: string;
  setFilterValue: (key: IbhFilterKeys, value: string | string[] | number | boolean | undefined | IbhDimensions) => void;
  updateMetricType: (value: boolean) => void;
  unitSystem: UnitSystem;
  onValidationChange?: (isValid: boolean) => void;
  onReset?: () => void;
}

export const DimensionForm = ({
  shapeKey,
  widenShortKey,
  setFilterValue,
  updateMetricType,
  unitSystem,
  onValidationChange,
  onReset,
}: DimensionFormProps) => {
  const { t } = useTranslation(['common', 'product/buyBox']);
  const query = useNextHashQueryParams();
  const dimensions = useDimensionsStore(state => state.dimensions);
  const setDimension = useDimensionsStore(state => state.setDimension);
  const { CustomEvent } = usePiwikPro();
  const resetDimensions = useDimensionsStore(state => state.reset);
  const setDimensionValue = useDimensionsStore(state => state.setDimensionsValue);
  const showDimensionsReset = useDimensionsStore(state => state.hasDimensionValues(shapeKey));
  const reset = useDebouncedCallback(() => resetDimensions());
  const updateDimensionsStore = useDebouncedCallback((usedShapeKey: string, key: DimensionKey, value: number) => {
    setDimensionValue(usedShapeKey, key, value);
  });
  const { addToast, reset: removeToast } = useToasts();

  useEffect(() => () => onValidationChange?.(true), [shapeKey, onValidationChange]);

  const validateSmallValues = (value: number, key: DimensionKey, formMetricType: UnitSystem) => {
    const dimensionsKeyToValidate = key === dimensionKey.d1 || key === dimensionKey.d2;
    const valueD1IsToSmall = key === dimensionKey.d1 && value < 1;
    const valueD2IsToSmall = key === dimensionKey.d2 && value < 2;
    const shouldRunValidation =
      formMetricType === UnitSystem.metric && dimensionsKeyToValidate && (valueD1IsToSmall || valueD2IsToSmall);

    if (!shouldRunValidation) {
      return;
    }

    const toSmallValueToastContent = (
      <div className="flex flex-row items-center">
        <div className="mr-4">
          <Icon size={9} id={IconID.INFO_CIRCLE_MODERN_FILLED} color={IconColor.WHITE} />
        </div>

        <div className="flex-col">
          <p className="mb-2 font-normal text-xl">
            {t('NOTIFICATION.DIMENSION_VALUE_TO_SMALL', { ns: 'bearing-hub/bearingHub' })}
          </p>
          <Button
            text={t('SWITCH_TO_IMPERIAL', { ns: 'bearing-hub/bearingHub' })}
            className="w-full"
            type={TYPE.SECONDARY}
            onClick={() => {
              updateMetricType(true);
              removeToast();
            }}
          />
        </div>
      </div>
    );

    addToast({
      content: toSmallValueToastContent,
      type: ToastType.INFO,
      position: 'relative top-72 mb-2 left-[calc(100vw-36rem)] w-[35rem]' as ToastPosition,
      allowMultiple: true,
    });
  };

  useEffect(() => {
    if (query.dimensions) {
      const dimensionParts = (query.dimensions as string).split('-');
      const queryDimensions = {} as IbhDimensions;
      if (dimensionParts.length > 0) {
        dimensionParts.forEach(dimensionPart => {
          const currentDimensionKey = dimensionPart.split(':')[0] as keyof IbhDimensions;
          const currentDimensionValue = dimensionPart.split(':')[1];

          if (currentDimensionValue !== '') {
            queryDimensions[currentDimensionKey] = parseFloat(currentDimensionValue);
          }
        });
        setDimension(shapeKey, queryDimensions);
      }
    } else {
      setDimension(shapeKey, {} as IbhDimensions);
    }
  }, [shapeKey]);

  const inputsDefinition: DimensionInput[] = [
    {
      title: 'BEARING_PARAMS_DEFINITION.SHAFT_DIAMETER',
      attribute: dimensionKey.d1,
      attributeText: 'd1',
      unit: getLengthCode(unitSystem),
      validation: (value: string) => Number(value) >= 0,
    },
    {
      title: 'BEARING_PARAMS_DEFINITION.OUTER_DIAMETER',
      attribute: dimensionKey.d2,
      attributeText: 'd2',
      unit: getLengthCode(unitSystem),
      validation: (value: string) => Number(value) >= 0,
    },
    {
      title: 'BEARING_PARAMS_DEFINITION.FLANGE_DIAMETER',
      attribute: dimensionKey.d3,
      attributeText: 'd3',
      unit: getLengthCode(unitSystem),
      validation: (value: string) => Number(value) >= 0,
    },
    {
      title: 'BEARING_PARAMS_DEFINITION.FLANGE_DIAMETER',
      attribute: dimensionKey.d4,
      attributeText: 'd4',
      unit: getLengthCode(unitSystem),
      validation: (value: string) => Number(value) >= 0,
    },
    {
      title: 'BEARING_PARAMS_DEFINITION.BEARING_WIDTH',
      attribute: dimensionKey.b1,
      attributeText: 'b1',
      unit: getLengthCode(unitSystem),
      validation: (value: string) => Number(value) >= 0,
    },
    {
      title:
        shapeKey === IbhShape.PR
          ? 'BEARING_PARAMS_DEFINITION.SLOT_WIDTH'
          : 'BEARING_PARAMS_DEFINITION.FLANGE_THICKNESS',
      attribute: dimensionKey.b2,
      attributeText: 'b2',
      unit: getLengthCode(unitSystem),
      validation: (value: string) => Number(value) >= 0,
    },
    {
      title: 'BEARING_PARAMS_DEFINITION.FLANGE_THICKNESS',
      attribute: dimensionKey.b2d3,
      attributeText: 'b2.2',
      unit: getLengthCode(unitSystem),
      validation: (value: string) => Number(value) >= 0,
    },
    {
      title: 'BEARING_PARAMS_DEFINITION.FLANGE_THICKNESS',
      attribute: dimensionKey.b2d4,
      attributeText: 'b2.1',
      unit: getLengthCode(unitSystem),
      validation: (value: string) => Number(value) >= 0,
    },
    {
      title: 'BEARING_PARAMS_DEFINITION.CLAMP_WIDTH',
      attribute: dimensionKey.b3,
      attributeText: 'b3',
      unit: getLengthCode(unitSystem),
      validation: (value: string) => Number(value) >= 0,
    },
    {
      title: 'BEARING_PARAMS_DEFINITION.SLOT_WIDTH',
      attribute: dimensionKey.sb,
      attributeText: 'sb',
      unit: getLengthCode(unitSystem),
      validation: (value: string) => Number(value) >= 0,
    },
    {
      title: 'BEARING_PARAMS_DEFINITION.SLOT_ANGLE',
      attribute: dimensionKey.slotAngle,
      attributeText: 'T',
      unit: getUnitLabel(AngleCode.DEGREE),
      validation: (value: string) => {
        const isValid = Number(value) >= 0 && Number(value) <= MAX_SLOT_ANGLE;
        onValidationChange?.(isValid);
        return isValid;
      },
    },
  ];

  const inputDefinitionForThrustwasher: DimensionInput = {
    title: 'BEARING_PARAMS_DEFINITION.BEARING_WIDTH',
    attribute: dimensionKey.b1,
    attributeText: 's',
    unit: getLengthCode(unitSystem),
    validation: (value: string) => Number(value) >= 0,
  };

  const drawing = (
    <div className="flex items-center lg:mb-0 mb-8 py-6">
      <WidenImage
        shortCode={widenShortKey}
        title={t(`SHAPES.${shapeKey}.TITLE`, { ns: 'bearing-hub/bearingHub' })}
        alt={t(`SHAPES.${shapeKey}.TITLE`, { ns: 'bearing-hub/bearingHub' })}
        filename={widenShortKey}
        width={265}
        height={125}
        crop
      />
    </div>
  );

  const formDefinition = ShapeDimensionsDefinitionForm[shapeKey];
  const form = formDefinition.map(definition => {
    const input =
      shapeKey === IbhShape.T && definition === dimensionKey.b1
        ? inputDefinitionForThrustwasher
        : inputsDefinition.find(el => el.attribute === definition);

    const initialValue = dimensions[shapeKey]?.[input.attribute as keyof IbhDimensions]?.toString() ?? '';

    return (
      input && (
        <div
          key={`${definition}-${shapeKey}`}
          className="mr-6 mt-3 g-gray-100"
          data-testid={`dimension-input-${input.attribute}`}
        >
          <label
            id="filter_label_surface_pressure"
            htmlFor={`filter_${input.attribute}`}
            className="block mb-2 truncate text-sm"
            title={`${t(input.title, { ns: 'bearing-hub/bearingHub' })} (${input.attributeText})`}
          >
            {t(input.title, { ns: 'bearing-hub/bearingHub' })} ({input.attributeText})
          </label>
          <div className="relative lg:w-60 w-64">
            <BasicNumberInput
              id={`filter_${input.attribute}`}
              unit={input.unit}
              placeholder={t(input.attributeText)}
              value={initialValue}
              allowEmpty
              validator={input.validation}
              errorMessage={
                input.attribute === dimensionKey.slotAngle
                  ? t('COMMON.QUANTITY_VALIDATION', {
                      min: 0,
                      max: MAX_SLOT_ANGLE,
                      ns: 'bearing-hub/bearingHub',
                    })
                  : undefined
              }
              onChange={value => {
                updateDimensionsStore(shapeKey, input.attribute, value !== '' ? Number(value) : null);
                if (Number(value) !== 0) {
                  validateSmallValues(Number(value), input.attribute, unitSystem);
                  setFilterValue(IbhFilterKeys.DIMENSIONS, {
                    ...dimensions[shapeKey],
                    [input.attribute]: Number(value),
                  });
                }
              }}
            />
          </div>
        </div>
      )
    );
  });

  const resetDimensionsButton = showDimensionsReset && (
    <div className="py-4">
      <FilterResetLink
        text={t('RESET_DIMENSIONS', { ns: 'bearing-hub/bearingHub' })}
        dataTestId="remove_dimensions_filters"
        reset={() => {
          CustomEvent.trackEvent(IbhEventCategory, TrackedIbhEventActions.removeAllDimensionsSelect);
          setFilterValue(IbhFilterKeys.DIMENSIONS, null);
          reset();
          onReset?.();
        }}
      />
    </div>
  );

  return (
    <div id="dimensions-form" className="flex flex-col bg-gray-50 p-6">
      <div className="grid grid-cols-1 sm:grid-cols-2 w-full gap-4">
        <div className="w-full">
          <div>
            <h4 className="mb-2">{t('DIMENSIONS_TITLE', { ns: 'bearing-hub/bearingHub' })}</h4>
            <p className="mb-2">{t('DIMENSIONS_INFO', { ns: 'bearing-hub/bearingHub' })}</p>
            <p>{t('DIMENSIONS_CUSTOM_SEARCH_INFO', { ns: 'bearing-hub/bearingHub' })}</p>
          </div>
          <Switch
            checked={unitSystem === UnitSystem.imperial}
            optionOne={t('COMMON.METRIC_TYPE.METRIC', { ns: 'bearing-hub/bearingHub' })}
            optionTwo={t('COMMON.METRIC_TYPE.IMPERIAL', { ns: 'bearing-hub/bearingHub' })}
            onChange={updateMetricType}
            theme="secondary"
          />
          <form
            className="col-span-3 flex flex-row flex-wrap md:justify-start justify-between"
            data-testid="dimension-form"
          >
            {form}
            {resetDimensionsButton}
          </form>
        </div>
        <div className="w-full flex justify-center">{drawing}</div>
      </div>
    </div>
  );
};
